import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import './AppLayout.css';

/**
 * AppLayout wraps all authenticated routes and provides:
 * - Persistent sidebar with history
 * - Top bar with navigation
 * - Outlet for child routes (checker, settings, etc.)
 *
 * Check history is stored in localStorage for privacy.
 * Chat sessions are persisted in Supabase.
 */
function AppLayout({
  user,
  onLogout,
  hasActiveSubscription,
  subscriptionTier,
  monthlyChecksUsed,
  monthlyCheckLimit,
  checksRemaining,
  checksResetDate,
  billingInterval,
  canPerformCheck,
  onCheckPerformed,
  subscriptionStatus,
  checkoutMessage,
  onDismissCheckoutMessage,
  profileLoading
}) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [history, setHistory] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [chatSessionsLoading, setChatSessionsLoading] = useState(false);
  const [currentCheckId, setCurrentCheckId] = useState(null);
  const [currentChatId, setCurrentChatId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get user-specific localStorage keys
  const getStorageKey = (key) => user?.id ? `${key}_${user.id}` : key;

  // Load check history from localStorage on mount or user change
  useEffect(() => {
    if (!user?.id) {
      setHistory([]);
      setChatSessions([]);
      return;
    }

    const savedHistory = localStorage.getItem(getStorageKey('complimind_history'));
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('Failed to parse history:', e);
        setHistory([]);
      }
    } else {
      setHistory([]);
    }
  }, [user?.id]);

  // Fetch chat sessions from Supabase on mount or user change
  useEffect(() => {
    if (!user?.id) {
      setChatSessions([]);
      return;
    }

    setChatSessionsLoading(true);
    fetch(`/api/chat-sessions?userId=${user.id}`)
      .then(r => r.json())
      .then(data => {
        // Map Supabase fields to match sidebar expectations
        const sessions = (data.sessions || []).map(s => ({
          id: s.id,
          title: s.title,
          timestamp: s.updated_at || s.created_at,
          itemType: 'chat'
        }));
        setChatSessions(sessions);
      })
      .catch(err => {
        console.error('Failed to load chat sessions:', err);
        setChatSessions([]);
      })
      .finally(() => setChatSessionsLoading(false));
  }, [user?.id]);

  // Save check history to localStorage when it changes
  useEffect(() => {
    if (!user?.id) return;
    if (history.length > 0) {
      localStorage.setItem(getStorageKey('complimind_history'), JSON.stringify(history));
    } else {
      localStorage.removeItem(getStorageKey('complimind_history'));
    }
  }, [history, user?.id]);

  const handleNewCheck = () => {
    setCurrentCheckId(null);
    // Navigate to checker if not already there
    if (location.pathname !== '/checker') {
      navigate('/checker');
    }
  };

  const handleNewChat = () => {
    setCurrentChatId(null);
    // Navigate to chat if not already there
    if (location.pathname !== '/chat') {
      navigate('/chat');
    }
  };

  const handleSaveCheck = (checkData) => {
    const newCheck = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      ...checkData
    };

    setHistory(prev => [newCheck, ...prev]);
    setCurrentCheckId(newCheck.id);
    return newCheck.id;
  };

  const handleSelectCheck = (checkId) => {
    setCurrentCheckId(checkId);
    setCurrentChatId(null);
    // Navigate to checker if not already there
    if (location.pathname !== '/checker') {
      navigate('/checker');
    }
  };

  const handleSelectChat = (chatId) => {
    setCurrentChatId(chatId);
    setCurrentCheckId(null);
    // Navigate to chat if not already there
    if (location.pathname !== '/chat') {
      navigate('/chat');
    }
  };

  const handleDeleteChat = (chatId) => {
    // Optimistic UI: remove immediately
    setChatSessions(prev => prev.filter(s => s.id !== chatId));
    if (currentChatId === chatId) {
      setCurrentChatId(null);
    }

    // Fire API call in background
    fetch('/api/chat-sessions', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, sessionId: chatId })
    }).catch(err => console.error('Failed to delete session:', err));
  };

  const handleRenameChat = (chatId, newTitle) => {
    // Optimistic UI: update immediately
    setChatSessions(prev => prev.map(s =>
      s.id === chatId ? { ...s, title: newTitle } : s
    ));

    // Fire API call in background
    fetch('/api/chat-sessions', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, sessionId: chatId, title: newTitle })
    }).catch(err => console.error('Failed to rename session:', err));
  };

  const handleDeleteCheck = (checkId) => {
    setHistory(prev => prev.filter(h => h.id !== checkId));
    if (currentCheckId === checkId) {
      setCurrentCheckId(null);
    }
  };

  const selectedCheck = currentCheckId ? history.find(h => h.id === currentCheckId) : null;

  // Clear current check/chat when navigating
  useEffect(() => {
    if (location.pathname !== '/checker') {
      setCurrentCheckId(null);
    }
    if (location.pathname !== '/chat') {
      setCurrentChatId(null);
    }
  }, [location.pathname]);

  // Check if we're on the settings page
  const isSettingsPage = location.pathname.startsWith('/settings');
  const isChatPage = location.pathname === '/chat';

  // Get page title based on current route
  const isDocumentsPage = location.pathname === '/documents';

  const getPageTitle = () => {
    if (isChatPage) return 'Compliance Chat';
    if (isDocumentsPage) return 'Documents';
    return null; // No title for checker (it has its own)
  };

  const pageTitle = getPageTitle();

  return (
    <div className="app-layout">
      {/* Sidebar - always visible */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        onLogout={onLogout}
        hasActiveSubscription={hasActiveSubscription}
        subscriptionTier={subscriptionTier}
        monthlyChecksUsed={monthlyChecksUsed}
        monthlyCheckLimit={monthlyCheckLimit}
        checksRemaining={checksRemaining}
        history={history}
        chatSessions={chatSessions}
        chatSessionsLoading={chatSessionsLoading}
        currentCheckId={currentCheckId}
        currentChatId={currentChatId}
        onNewCheck={handleNewCheck}
        onNewChat={handleNewChat}
        onSelectCheck={handleSelectCheck}
        onDeleteCheck={handleDeleteCheck}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        onRenameChat={handleRenameChat}
        onNavigateSettings={() => navigate('/settings')}
        subscriptionStatus={subscriptionStatus}
        profileLoading={profileLoading}
      />

      {/* Main Content Area */}
      <div className={`app-main-wrapper ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Top Bar - always visible except settings */}
        {!isSettingsPage && (
          <div className="app-topbar">
            {/* Page title or logo */}
            {pageTitle ? (
              <div className="topbar-title">
                {isChatPage ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                ) : isDocumentsPage ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                  </svg>
                ) : null}
                <span>{pageTitle}</span>
                {isChatPage && <span className="beta-tag">Beta</span>}
              </div>
            ) : !sidebarOpen ? (
              <span className="topbar-logo">Mindmark</span>
            ) : null}

            {/* Center - Upgrade button */}
            <div className="topbar-center">
              {subscriptionTier === 'free' && (
                <button className="get-pro-btn" onClick={() => navigate('/pricing')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                  </svg>
                  Upgrade
                </button>
              )}
              {subscriptionTier === 'basic' && (
                <button className="get-pro-btn" onClick={() => navigate('/pricing')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                  </svg>
                  Upgrade to Pro
                </button>
              )}
            </div>

            <a className="help-button" href="/faq" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Help
            </a>
          </div>
        )}

        {/* Content area */}
        <div className="app-content">
          {/* Outlet renders the child route (CheckerMain, Chat, or SettingsPage) */}
          <Outlet context={{
          user,
          hasActiveSubscription,
          subscriptionTier,
          monthlyChecksUsed,
          monthlyCheckLimit,
          checksRemaining,
          checksResetDate,
          billingInterval,
          canPerformCheck,
          onCheckPerformed,
          onSaveCheck: handleSaveCheck,
          selectedCheck,
          onNewCheck: handleNewCheck,
          onLogout,
          checkoutMessage,
          onDismissCheckoutMessage,
          sidebarOpen,
          onToggleSidebar: () => setSidebarOpen(!sidebarOpen),
          // Chat-specific context
          currentChatId,
          chatSessions,
          onSaveChat: (chatData) => {
            // Add or update chat session metadata in local state
            setChatSessions(prev => {
              const existing = prev.find(c => c.id === chatData.id);
              if (existing) {
                return prev.map(c => c.id === chatData.id ? { ...c, ...chatData } : c);
              } else {
                return [{ ...chatData, itemType: 'chat' }, ...prev];
              }
            });
            setCurrentChatId(chatData.id);
          },
          onNewChat: handleNewChat
        }} />
        </div>
      </div>
    </div>
  );
}

export default AppLayout;
